import { BigNumber } from '@ethersproject/bignumber'
import { AxiosError } from 'axios'
import { ButtonPrimary } from 'components/Button'
import { getChainId } from 'constants/chains'
import useParticle from 'hooks/useParticle'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { routes } from '../../../../constants/routes'
import { useClaimNFT } from '../../hooks/useClaimNFT'
import { useRedeemNFTByNFCCode } from '../../hooks/useRedeemNFTByNFCCode'
import { RedeemErrors, RedeemErrorsMessage } from '../../models/RedeemErrors'
import { ClaimNFTModal } from '../ClaimNFTModal/ClaimNFTModal'
import { WarningModal } from '../WarningModal/WarningModal'

const messages = {
  signature: 'Please proceed by signing the message on the wallet',
  register: 'Please complete the registration process before proceeding to claim.',
}

export const ClaimNFTButton = ({ className }: { className?: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRegisterModalOpen, setRegisterModal] = useState(false)
  const history = useHistory()
  const { account, provider, chainId, authToken, getSmartAccount } = useParticle()
  const defaultChain = getChainId(process.env.REACT_APP_CHAIN_ID)
  const isValidChain = useMemo(() => !Boolean(chainId) || chainId === defaultChain, [chainId])

  const {
    mutateAsync: mintNFT,
    isError: isMintError,
    error: claimNFTError,
    reset: claimReset,
    isLoading: isClaimingNFT,
  } = useClaimNFT()
  const {
    data: claimedNFT,
    mutate: claimNFT,
    isSuccess,
    isError,
    reset: redeemReset,
    error: redeemNFTError,
    isLoading: isRedeemingNFT,
  } = useRedeemNFTByNFCCode({
    onSuccess: async ({ signature, tokenInformation }) => {
      await mintNFT({ signature, tokenInformation })
    },
  })

  const reset = useCallback(() => {
    claimReset()
    redeemReset()
  }, [claimReset, redeemReset])

  const handleClaimNft = useCallback(
    async (code: string) => {
      if (code.includes('_')) return

      const smartAccount = getSmartAccount?.()
      const hasAA = !!smartAccount

      if (!hasAA) {
        throw new Error('NOT_CONNECTED_TO_PAY_MASTER')
      }

      const aaAddress = await smartAccount.getAddress()
      claimNFT({ code, userAAAdress: aaAddress })
    },
    [claimNFT, getSmartAccount]
  )

  const handleToggleModal = useCallback(() => {
    if (provider && isValidChain && authToken) {
      setIsModalOpen(!isModalOpen)
      reset()
    } else {
      setRegisterModal(true)
    }
  }, [reset, setIsModalOpen, isModalOpen, isValidChain, provider, authToken])

  const errorMessage: string = useMemo(() => {
    if (redeemNFTError) {
      if (redeemNFTError instanceof AxiosError) {
        if (redeemNFTError?.response?.status === 401) {
          return RedeemErrorsMessage.UNAUTHORIZED
        }

        if (redeemNFTError?.response?.data?.errorCode) {
          const { errorCode } = redeemNFTError?.response?.data

          return (
            {
              [RedeemErrors.NFC_CODE_NOT_FOUND]: RedeemErrorsMessage.NFC_CODE_NOT_FOUND,
              [RedeemErrors.TOKEN_NOT_CLAIMABLE]: RedeemErrorsMessage.TOKEN_NOT_CLAIMABLE,
            }[errorCode] || RedeemErrorsMessage.UNEXPECTED_ERROR
          )
        }
      } else if (
        String((redeemNFTError as any)?.data?.extraMessage?.message ?? '').includes(
          'PhygitalItems:claimNft:: This token is not claimable.'
        )
      ) {
        return RedeemErrorsMessage.TOKEN_NOT_CLAIMABLE
      }
    }

    if (claimNFTError) {
      if (String(claimNFTError?.code) === '4001') {
        return RedeemErrorsMessage.TRANSACTION_SIGNING_REJECTED
      }
    }

    return RedeemErrorsMessage.UNEXPECTED_ERROR
  }, [redeemNFTError, claimNFTError])

  const handleSeeNFT = useCallback(() => {
    setIsModalOpen((oldState) => !oldState)

    if (claimedNFT) {
      history.push(
        routes.NFTPreview.replace('{collectionAddress}', claimedNFT.tokenInformation.collectionAddress).replace(
          '{tokenId}',
          BigNumber.from(claimedNFT.tokenInformation.tokenId.hex).toString()
        )
      )
    }
  }, [claimedNFT, history])

  return (
    <>
      <ButtonPrimary onClick={handleToggleModal} className={className}>
        Claim NFT
      </ButtonPrimary>
      <ClaimNFTModal
        isOpen={isModalOpen}
        onClose={handleToggleModal}
        onSubmit={handleClaimNft}
        isSuccess={isSuccess}
        isError={isError || isMintError}
        errorMessage={errorMessage}
        successMessage="You have successfully claimed the NFT."
        heading="Claim the NFT"
        subheading="Scan the NFC tag in the item and enter the verification code to assign it."
        onRetry={reset}
        onSeeNFT={handleSeeNFT}
        isLoading={isRedeemingNFT || isClaimingNFT}
      />

      {isRegisterModalOpen && (
        <WarningModal
          message={!authToken && account ? messages.signature : messages.register}
          onClose={() => setRegisterModal(false)}
        />
      )}
    </>
  )
}
